import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { Button } from "./ui/button";

const NAV_LINKS = [
  { label: "Home", to: "/", section: "home" },
  { label: "About Us", to: "/#about", section: "about" },
  { label: "Our Services", to: "/#services", section: "services" },
  { label: "Contact Us", to: "/#contact", section: "contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (e, link) => {
    setOpen(false);
    if (link.to.startsWith("/#")) {
      e.preventDefault();
      const id = link.to.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 80);
      } else {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed left-0 right-0 top-0 z-50 transition-all ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <Link
          to="/"
          data-testid="nav-home-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.to}
              onClick={(e) => handleNav(e, link)}
              data-testid={`nav-link-${link.section}`}
              className="group relative text-sm font-medium text-slate-700 transition-colors hover:text-[#00A3E0]"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[#00A3E0] transition-all group-hover:w-full" />
            </a>
          ))}
          <Button
            asChild
            data-testid="nav-submit-project-btn"
            className="rounded-full bg-[#00A3E0] px-5 text-white shadow-lg shadow-[#00A3E0]/25 hover:bg-[#0089bd]"
          >
            <Link to="/submit-project" className="flex items-center gap-1.5">
              Submit Your Project
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Button>
        </nav>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full border border-slate-200 bg-white p-2 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="border-t border-slate-200 bg-white lg:hidden" data-testid="nav-mobile-menu">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {NAV_LINKS.map((link) => (
              <a
                key={link.label}
                href={link.to}
                onClick={(e) => handleNav(e, link)}
                data-testid={`nav-link-mobile-${link.section}`}
                className="rounded-lg px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/submit-project"
              onClick={() => setOpen(false)}
              data-testid="nav-submit-project-mobile-btn"
              className="mt-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#00A3E0] px-5 py-3 text-sm font-semibold text-white"
            >
              Submit Your Project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
